<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import DolphinRadioButton from '@/components/atoms/DolphinRadioButton.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import LineRichMenuFormStatus from './LineRichMenuFormStatus.vue'
import LineRichMenuFormTemplate from './LineRichMenuFormTemplate.vue'
import LineRichMenuFormTemplateAction from './LineRichMenuFormTemplateAction.vue'
import LineRichMenuFormImage from './LineRichMenuFormImage.vue'
import LineRichMenuFormIsDisplayDefault from './LineRichMenuFormIsDisplayDefault.vue'
import LineRichMenuFormErrorMessage from './LineRichMenuFormErrorMessage.vue'
import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'
import type {
  LineRichMenuFormType,
  LineRichMenuFormErrors,
  LineRichMenuFormErrorManager,
  LineRichMenuFormErrorPath,
  LineRichMenuFormTemplateActionType,
} from './formSchema'
import {
  LineRichMenuFormActionTypeUnselected,
  LineRichMenuFormStatusPublished,
  validPublishedLineRichMenuFormSchema,
  validUnpublishedLineRichMenuFormSchema,
} from './formSchema'
import { useIssueLineRichMenuBackgroundImageUploadUrlMutation } from './LineRichMenuForm.generated'
import {
  LineRichMenuActionType,
  LineRichMenuStatus,
  type LineRichMenuInput,
  type LineRichMenuTemplateAction,
  TargetType,
  ChatRoomStatus,
} from '@/types/schema.generated'
import * as v from 'valibot'
import { useAlert } from '@/libs/alertPortal'
import SegmentForm, {
  type UserVariableDefinitionOptionType,
} from '@/features/segment/components/SegmentForm/SegmentForm.vue'
import type { SegmentItemType } from '@/features/segment/components/SegmentForm/SegmentFormItem.vue'
import {
  allChatRoomStatuses,
  chatRoomStatusString,
} from '@/models/chatRoom/chatRoomStatus'

const alert = useAlert()

const props = withDefaults(
  defineProps<{
    initialForm?: LineRichMenuFormType
    isSubmitting: boolean
    userRegistrationInflowSourceDefinitions?: {
      guid: string
      inflowSourceName: string
    }[]
    clickURLs?: { guid: string; url: string }[]
    plannerEnquetes?: { guid: string; title: string }[]
  }>(),
  {
    initialForm: () => ({
      label: '',
      status: LineRichMenuFormStatusPublished,
      templateName: 'large_201',
      backgroundImage: null,
      templateActions: [],
      chatBarText: 'メニュー',
      isDisplayDefault: true,
      targetType: TargetType.AllFriends,
      segment: undefined,
    }),
    userRegistrationInflowSourceDefinitions: () => [],
    clickURLs: () => [],
    plannerEnquetes: () => [],
  }
)

const targetOptions = [
  {
    type: 'ALL_FRIENDS',
    label: '友だち全員',
    handler: () => {
      form.value.targetType = TargetType.AllFriends
      form.value.segment = undefined
    },
  },
  {
    type: 'ALL_MATCHED_USERS',
    label: 'マッチしているすべてのユーザ',
    handler: () => {
      form.value.targetType = TargetType.AllMatchedUsers
      form.value.segment = undefined
    },
  },
  {
    type: 'SEGMENT',
    label: 'セグメント',
    handler: () => {
      form.value.targetType = TargetType.Segment
      form.value.segment = {
        type: 'segment',
        segment: {
          segmentConditions: [],
        },
      }
    },
  },
]

const emit = defineEmits<{
  (event: 'submit', input: LineRichMenuInput): void
  (event: 'update:isSubmitting', value: boolean): void
}>()

const form = ref<LineRichMenuFormType>({ ...props.initialForm })
const formErrors = ref<LineRichMenuFormErrors | undefined>()
const formErrorManager: LineRichMenuFormErrorManager = {
  get: (path: LineRichMenuFormErrorPath): string | undefined => {
    const errors = formErrors.value
    if (!errors) {
      return undefined
    }

    const messages = errors.nested?.[path]
    if (!messages) {
      return undefined
    }

    return messages[0]
  },
  getStartsWith: (path: string): string | undefined => {
    const errors = formErrors.value
    if (!errors) {
      return undefined
    }

    const nested = errors.nested
    if (nested === undefined) {
      return undefined
    }

    const keys = Object.keys(nested).filter(key => key.startsWith(path))
    if (keys.length === 0) {
      return undefined
    }

    const key = keys[0] as keyof typeof nested
    if (key === undefined) {
      return undefined
    }

    const errorMessages = nested[key]
    if (errorMessages !== undefined) {
      return errorMessages[0]
    }

    return undefined
  },
}
const showValidate = ref(false)
const actionCount = computed(() => {
  switch (form.value.templateName) {
    case 'large_201':
      return 6
    case 'large_202':
      return 4
    case 'large_203':
      return 4
    case 'large_204':
      return 3
    case 'large_205':
      return 2
    case 'large_206':
      return 2
    case 'large_207':
      return 1
    case 'small_101':
      return 3
    case 'small_102':
      return 2
    case 'small_103':
      return 2
    case 'small_104':
      return 2
    case 'small_105':
      return 1
    default:
      return 0
  }
})

const issueLineRichMenuBackgroundImageUploadUrl =
  useIssueLineRichMenuBackgroundImageUploadUrlMutation()

const panelNames = ['A', 'B', 'C', 'D', 'E', 'F']
// テンプレートアクションの数が変更されたときに、form.value.templateActions を更新する
const updateTemplateActions = () => {
  const newTemplateActions: LineRichMenuFormTemplateActionType[] = [
    ...form.value.templateActions,
  ]
  const existingPanelNamesInModel = newTemplateActions.map(
    (action: LineRichMenuFormTemplateActionType) => action.panelName
  )
  // eslint-disable-next-line fp/no-let
  for (let i = 0; i < actionCount.value; i++) {
    if (!existingPanelNamesInModel.includes(panelNames[i])) {
      // eslint-disable-next-line fp/no-mutating-methods
      newTemplateActions.push({
        panelName: panelNames[i],
        actionType: LineRichMenuFormActionTypeUnselected,
        url: '',
        label: '',
        text: '',
      })
    }

    // アルファベット順に並び替え
    // eslint-disable-next-line fp/no-mutating-methods
    newTemplateActions.sort(
      (
        a: LineRichMenuFormTemplateActionType,
        b: LineRichMenuFormTemplateActionType
      ) => a.panelName.localeCompare(b.panelName)
    )

    // actionCount と model.value の長さが異なる場合、model.value の要素をうしろから削除する
    if (newTemplateActions.length > actionCount.value) {
      // eslint-disable-next-line fp/no-mutating-methods
      newTemplateActions.splice(actionCount.value)
    }

    form.value.templateActions = newTemplateActions
  }
}

watch(
  actionCount,
  () => {
    updateTemplateActions()
  },
  { immediate: true }
)

watch(form.value, () => {
  if (showValidate.value) {
    const result = validate()
    if (!result.success) {
      formErrors.value = result.errors
    }
  } else {
    formErrors.value = undefined
  }
})

const handleSubmit = async (e: Event) => {
  e.preventDefault()
  showValidate.value = true
  emit('update:isSubmitting', true)

  try {
    if (form.value.backgroundImage) {
      if (
        form.value.backgroundImage.image !==
          props.initialForm.backgroundImage?.image &&
        form.value.backgroundImage.image instanceof File
      ) {
        const uploadedImageFileName = await uploadImage(
          form.value.backgroundImage.image
        )
        if (uploadedImageFileName) {
          form.value.backgroundImage.name = uploadedImageFileName
        }
      }
    } else {
      if (form.value.status === LineRichMenuFormStatusPublished) {
        throw new Error('画像が設定されていません')
      }
    }
  } catch (e) {
    emit('update:isSubmitting', false)
    if (e instanceof Error) {
      alert.error('画像が設定されていません')
      return
    }
    console.error(e)
    return
  }

  const result = validate()
  if (!result.success) {
    console.error(result.errors)
    emit('update:isSubmitting', false)
    formErrors.value = result.errors
    alert.error('入力内容に誤りがあります')
    return
  }

  const filteredTemplateActions = result.output.templateActions.filter(
    (templateAction: {
      panelName: string
      actionType: 'link' | 'text' | 'unselected'
      label?: string | undefined
      text?: string | undefined
      url?: string | undefined
    }) => templateAction.actionType !== LineRichMenuFormActionTypeUnselected
  )

  const parsedTemplateActions = parseTemplateActions(filteredTemplateActions)

  const parsedOutput: LineRichMenuInput = {
    label: result.output.label,
    status: result.output.status,
    template: result.output.template,
    backgroundImageFileName: result.output.backgroundImageFileName,
    templateActions: parsedTemplateActions,
    chatBarText: result.output.chatBarText,
    isDisplayDefault: result.output.isDisplayDefault,
    targetType: result.output.targetType,
    segment: result.output.segment,
  }

  showValidate.value = false
  emit('submit', parsedOutput)
}

type ValidateResponse =
  | {
      success: true
      output: v.InferOutput<
        | typeof validPublishedLineRichMenuFormSchema
        | typeof validUnpublishedLineRichMenuFormSchema
      >
    }
  | {
      success: false
      errors: LineRichMenuFormErrors
    }

const validate = (): ValidateResponse => {
  const status: LineRichMenuStatus =
    form.value.status === LineRichMenuFormStatusPublished
      ? LineRichMenuStatus.Published
      : LineRichMenuStatus.Unpublished

  const schema =
    form.value.status === LineRichMenuFormStatusPublished
      ? validPublishedLineRichMenuFormSchema
      : validUnpublishedLineRichMenuFormSchema

  const segment = (() => {
    const conditions = form.value?.segment?.segment?.segmentConditions
    if (conditions === undefined) {
      return undefined
    }
    return {
      conditions: conditions.map(condition => {
        switch (condition.type) {
          case 'line-activation-login-link':
            return {
              loginLinkCondition: {
                not: condition.not,
              },
            }
          case 'inflow-source':
            return {
              inflowSourceCondition: {
                inflowSourceDefinitionGUID:
                  condition.inflowSourceDefinitionGUID,
                not: condition.not,
              },
            }
          case 'click-url':
            return {
              clickURLCondition: {
                url: condition.url,
                not: condition.not,
              },
            }
          case 'planner-enquete-answered':
            return {
              enqueteAnsweredCondition: {
                enqueteGUID: condition.plannerEnqueteGUID,
                not: condition.not,
              },
            }
          case 'room-status':
            return {
              roomStatusCondition: {
                status: condition.status,
                not: condition.not,
              },
            }
        }
      }),
    }
  })()

  const result = v.safeParse(schema, {
    label: form.value.label,
    status: status,
    template: form.value.templateName,
    backgroundImageFileName: form.value.backgroundImage?.name ?? '',
    templateActions: form.value.templateActions,
    chatBarText: form.value.chatBarText,
    isDisplayDefault: form.value.isDisplayDefault,
    targetType: form.value.targetType,
    segment: segment,
  })

  formErrors.value = undefined

  if (result.success) {
    return {
      success: true,
      output: result.output,
    }
  } else {
    return {
      success: false,
      errors: v.flatten(result.issues),
    }
  }
}

const parseTemplateActions = (
  templateActions: {
    panelName: string
    actionType: 'link' | 'text' | 'unselected'
    label?: string | undefined
    text?: string | undefined
    url?: string | undefined
  }[]
): LineRichMenuTemplateAction[] => {
  if (templateActions.length === 0) {
    return []
  }

  return templateActions.map(templateAction => {
    switch (templateAction.actionType) {
      case 'link':
        if (!templateAction.url || !templateAction.label) {
          throw new Error('URL またはアクションラベルが設定されていません')
        }
        return {
          panelName: templateAction.panelName,
          actionType: LineRichMenuActionType.Link,
          url: templateAction.url,
          label: templateAction.label,
        }
      case 'text':
        if (!templateAction.text) {
          throw new Error('テキストが設定されていません')
        }
        return {
          panelName: templateAction.panelName,
          actionType: LineRichMenuActionType.Text,
          text: templateAction.text,
        }
      default:
        throw new Error('アクションが選択されていません')
    }
  })
}

const uploadImage = async (file: File): Promise<string> => {
  const fileExtension = (() => {
    const array = file.name.split('.')
    return array[array.length - 1]
  })()

  const res = await issueLineRichMenuBackgroundImageUploadUrl.mutate({
    input: {
      fileExtension: fileExtension,
    },
  })
  if (!res) {
    throw new Error('failed to get res')
  }
  if (res?.errors || res?.data == undefined) {
    console.error(res.errors)
    throw new Error('failed to get res.data')
  }

  await fetch(res.data.issueLineRichMenuBackgroundImageUploadUrl.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  })

  return res.data.issueLineRichMenuBackgroundImageUploadUrl.fileName
}

const segmentTypeOptions: {
  text: string
  value: SegmentItemType
}[] = [
  {
    text: 'ログイン連携',
    value: 'line-activation-login-link',
  },
  {
    text: '友だち追加経路',
    value: 'inflow-source',
  },
  {
    text: 'URLクリック',
    value: 'click-url',
  },
  {
    text: 'アンケート回答',
    value: 'planner-enquete-answered',
  },
  {
    text: '対応状況',
    value: 'room-status',
  },
]

const inflowSourceOptions = computed(() => {
  return props.userRegistrationInflowSourceDefinitions.map(source => ({
    text: source.inflowSourceName,
    value: source.guid,
  }))
})

const clickUrlOptions = computed(() => {
  return props.clickURLs.map(url => ({
    text: url.url,
    value: url.url,
  }))
})

const plannerEnqueteOptions = computed(() => {
  return props.plannerEnquetes.map(enquete => ({
    text: enquete.title,
    value: enquete.guid,
  }))
})

// 未設定は選択肢に含めない
const roomStatusOptions = allChatRoomStatuses
  .filter(status => status !== ChatRoomStatus.NotDefined)
  .map(status => ({
    text: chatRoomStatusString[status],
    value: status,
  }))

const userVariableDefinitionOptions: UserVariableDefinitionOptionType[] = []

const errorMessageLabel = computed(() =>
  formErrorManager.getStartsWith('label')
)
const errorMessageChatBarText = computed(() =>
  formErrorManager.get('chatBarText')
)
const errorMessageSegment = computed(() =>
  formErrorManager.getStartsWith('segment')
)
</script>

<template>
  <div :class="$style.form">
    <h1>LINEリッチメニュー設定</h1>
    <section>
      <p :class="$style.sectionTitle">ラベル</p>
      <div :class="$style.sectionContent">
        <DolphinTextInput v-model="form.label" :class="$style.labelInput" />
        <LineRichMenuFormErrorMessage :errorMessage="errorMessageLabel" />
      </div>
    </section>
    <section>
      <p :class="$style.sectionTitle">ターゲット</p>
      <div :class="$style.sectionContent">
        <div :class="$style.optionsWrapper">
          <label
            v-for="target of targetOptions"
            :key="target.type"
            :class="$style.option"
          >
            <DolphinRadioButton
              name="target"
              :value="target.type"
              :modelValue="form.targetType"
              @update:modelValue="target.handler"
            />
            <span :class="$style.option__text">
              {{ target.label }}
            </span>
          </label>
        </div>
      </div>
      <SegmentForm
        v-if="form.targetType === TargetType.Segment && form.segment != null"
        v-model="form.segment"
        :segmentTypeOptions="segmentTypeOptions"
        :inflowSourceOptions="inflowSourceOptions"
        :clickUrlOptions="clickUrlOptions"
        :plannerEnqueteOptions="plannerEnqueteOptions"
        :roomStatusOptions="roomStatusOptions"
        :userVariableDefinitionOptions="userVariableDefinitionOptions"
        :class="$style.customTargetSetting"
      />
      <LineRichMenuFormErrorMessage :errorMessage="errorMessageSegment" />
    </section>
    <LineRichMenuFormStatus v-model="form.status" />
    <LineRichMenuFormTemplate
      v-model:template-name="form.templateName"
      v-model:action-count="actionCount"
      :uploaded-image-size="form.backgroundImage?.size ?? null"
    />
    <LineRichMenuFormImage
      v-model="form.backgroundImage"
      :template-name="form.templateName"
    />
    <LineRichMenuFormTemplateAction
      v-model="form.templateActions"
      :form-error-manager="formErrorManager"
    />
    <section>
      <p :class="$style.sectionTitle">メニューバーのテキスト</p>
      <div :class="$style.sectionContent">
        <DolphinTextInput
          v-model="form.chatBarText"
          :class="$style.chatBarTextInput"
        />
        <LineRichMenuFormErrorMessage :errorMessage="errorMessageChatBarText" />
      </div>
    </section>
    <LineRichMenuFormIsDisplayDefault v-model="form.isDisplayDefault" />
    <div :class="$style.submitButtonWrapper">
      <FlatButton
        :disabled="isSubmitting"
        @click="handleSubmit"
        variant="primary"
        size="large"
      >
        <LoadingIndicator v-if="isSubmitting" />
        保存
      </FlatButton>
    </div>
  </div>
</template>

<style lang="scss" module>
.form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.submitButtonWrapper {
  display: flex;
  justify-content: center;
}

.labelInput {
  width: 100%;
  margin-top: 4px;
}

.chatBarTextInput {
  width: 40%;
  margin-top: 4px;
}

.customTargetSetting {
  margin-top: 16px;
}

.sectionTitle {
  font-size: 18px;
  font-weight: bold;
}

.sectionContent {
  margin-top: 10px;
}

.optionsWrapper {
  display: flex;
  gap: 24px;
}

.option {
  display: inline-flex;
  align-items: center;

  &__text {
    font-size: 14px;

    &:nth-child(n + 2) {
      margin-left: 4px;
    }
  }
}
</style>
